import React, { useState, useEffect, useRef } from 'react';
import { api, clearSessionToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion } from 'framer-motion';
import { Shield, Loader2, Smartphone } from 'lucide-react';

export default function TwoFactorChallenge() {
  const { refresh } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (code.replace(/\D/g, '').length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/2fa/challenge', { code });
      if (refresh) await refresh();
      window.location.href = '/app/dashboard';
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid code — check your authenticator app and try again');
      setCode('');
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally { setBusy(false); }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearSessionToken();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen grid place-items-center p-6" data-testid="two-fa-challenge-page">
      <div className="ambient-bg"/>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-md p-10"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center">
            <Shield className="text-white" size={22}/>
          </div>
          <div>
            <div className="overline">Two-factor authentication</div>
            <h1 className="font-serif text-2xl">Enter your code</h1>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4 mb-6">
          <Smartphone size={16} className="text-slate-500 mt-0.5 shrink-0"/>
          <div className="text-xs text-slate-600 leading-relaxed">
            Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and enter the 6-digit code shown for <b>Iris Realty</b>.
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            autoComplete="one-time-code"
            data-testid="2fa-code-input"
            className="w-full text-center tracking-[0.5em] text-2xl font-serif px-4 py-4 rounded-xl border-2 border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#679286]/40 focus:border-[#679286]"
          />
          {error && <div data-testid="2fa-error" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 text-center">{error}</div>}
          <button type="submit" disabled={busy || code.length !== 6}
            data-testid="2fa-submit-btn"
            className="w-full py-3 rounded-full bg-slate-900 text-white text-sm flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin"/>}
            Verify & continue
          </button>
        </form>

        <button type="button" onClick={logout}
          className="w-full mt-4 py-2 text-xs text-slate-500 hover:text-slate-700">
          Sign out instead
        </button>
      </motion.div>
    </div>
  );
}
